import * as React from 'react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import users from 'modules/core/store/collections/users'

import useCanSeeApprovalRequest from 'modules/contracts/hooks/useCanSeeApprovalRequest'
import { ApprovalStatus } from 'modules/contracts/lib/enums'
import { ContractVersionRequests } from 'modules/contracts/store/collections/contractVersionRequests'
import type ContractVersionRequest from 'modules/contracts/store/models/ContractVersionRequest'
import type Employee from 'modules/employees/store/models/Employee'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

const useApprovePage = (employee: Employee, requestContractVersionId: number) => {
  // fetch request data
  const {
    contractVersionRequest,
    notFound,
    loading: loadingData,
  } = useFetchRequest(requestContractVersionId)

  // get requester data
  const requester_id = contractVersionRequest?.attributes.approval_author_id
  const requester = users.get(requester_id)

  // load can see page
  const { loading: loadingPermissions, canSeeRequest } = useCanSeePage(
    employee,
    contractVersionRequest
  )

  const canSeePage = canSeeRequest && !notFound
  const loading = loadingData || loadingPermissions

  return {
    loading,
    canSeePage,
    contractVersionRequest,
    requester,
  }
}

const useCanSeePage = (
  employee: Employee,
  contractVersionRequest?: ContractVersionRequest | null
) => {
  const { loading } = useAsyncData(async () => {
    await Promise.all([policyAuthorizations.authorizePolicies('contracts.promote', [employee])])
  }, [employee.id])

  const canPromote = policyAuthorizations.for('contracts.promote', employee)
  const canSeeRequest = useCanSeeApprovalRequest({
    contractVersionRequest,
    canPromote,
  })
  return {
    loading,
    canSeeRequest,
  }
}

const useFetchRequest = (requestContractVersionId: number) => {
  // states
  const contractVersionRequests = useLazyRef(() => new ContractVersionRequests()).current
  const [contractVersionRequest, setContractVersionRequest] =
    React.useState<ContractVersionRequest | null>()
  const [notFound, setNotFound] = React.useState<boolean>(false)

  // load request
  const { loading } = useAsyncData(async () => {
    await contractVersionRequests.fetchWithApprovalStatusByIds(
      [requestContractVersionId],
      [ApprovalStatus.pending, ApprovalStatus.rejected, ApprovalStatus.approved]
    )
    setNotFound(!contractVersionRequests.at(0))
    setContractVersionRequest(contractVersionRequests.at(0))
  }, [requestContractVersionId])

  return {
    contractVersionRequest,
    notFound,
    loading,
  }
}

export default useApprovePage
